import { useState } from 'react';
import { ChangeRequestStatus } from '../../../generated/api/changeRequestStatus.js';
import { SubscriptionVersion } from '../Components/SubscriptionScheduledChangeDisclaimer.js';
import type { BillingAccountsResponse } from '../../../generated/api/billingAccountsResponse.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { GetSubscriptionScheduledChanges } from '../../../generated/api/getSubscriptionScheduledChanges.js';
import type { Subscription } from '../../../generated/api/subscription.js';
import type { SubscriptionScheduledChange } from '../../../generated/api/subscriptionScheduledChange.js';

export interface ScheduledChangeState {
  scheduledChange?: SubscriptionScheduledChange;
  scheduledSubscription?: Subscription;
  subscriptionVersion: SubscriptionVersion;
  onSubscriptionVersionChange: (version: SubscriptionVersion) => void;
}

export const useScheduledChange = (
  subscription: Subscription,
  billingAccounts: BillingAccountsResponse,
  contacts: Contact[],
  subscriptionScheduledChanges?: GetSubscriptionScheduledChanges
): ScheduledChangeState => {
  const [subscriptionVersion, setSubscriptionVersion] = useState(SubscriptionVersion.ACTIVE);

  const scheduledChange = subscriptionScheduledChanges?.subscriptionScheduledChangeResponses?.find(
    change => change.changeRequestStatus === ChangeRequestStatus.PENDING
  );
  const changeRequest = scheduledChange?.subscriptionChangeOwnerAccountRequest;

  const targetBillingAccount = billingAccounts?.searchResults?.find(
    ba => ba.result.billingAccountId === changeRequest?.targetBillingAccount
  );

  const targetContactId = changeRequest?.targetContactDetails?.contactId;
  const scheduledChangeContact = targetContactId ? contacts.find(c => c.contactId === targetContactId) : undefined;

  const scheduledSubscription: Subscription | undefined = scheduledChange
    ? {
        ...subscription,
        billingAccountId: changeRequest?.targetBillingAccount ?? subscription.billingAccountId,
        billingAccountDisplayId:
          targetBillingAccount?.result.billingAccountDisplayId ?? subscription.billingAccountDisplayId,
        subscriptionContactId: targetContactId,
        subscriptionContactName: scheduledChangeContact
          ? `${scheduledChangeContact.person?.firstName} ${scheduledChangeContact.person?.lastName}`
          : subscription.subscriptionContactName,
        subscriptionPurposeOfUse: changeRequest?.targetContactDetails?.purposeOfUse,
        costCenter: changeRequest?.costCenter ?? subscription.costCenter,
        subscriptionReference: changeRequest?.subscriptionReference ?? subscription.subscriptionReference,
      }
    : undefined;

  return {
    scheduledChange,
    scheduledSubscription,
    subscriptionVersion,
    onSubscriptionVersionChange: setSubscriptionVersion,
  };
};
